import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import PayoutBreakdownEditor from "@/components/PayoutBreakdownEditor";

/**
 * Edit Passthrough Payout Dialog — opens when admin clicks the pencil icon
 * on a passthrough row in the Assigned Brands table. Updates the per-action
 * payout breakdown so publisher feed ingestion stays parseable.
 *
 * THREE branches:
 *   • CPC mode (`_cpcMode`)         → split + display label + pub_flat_amount
 *   • Fixed-rate cap (`capMode`)    → Best-Buy-style margin guard. Switches
 *     the assignment to `payout_mode=fixed_pct` with `custom_commission_pct
 *     = cap`. Math becomes `min(sale × cap%, network_payout)` so low-rate
 *     sales pass through 100% (no extra shave) and high-rate sales are
 *     capped at the configured pub rate (we keep the difference).
 *     Use this for brands where the network pays variable but you want
 *     to advertise a stable pub rate (Wayfair → 8.6%, etc.).
 *   • Passthrough (default)          → payout breakdown rows + split %.
 */
export function PassthroughEditDialog({
  editingPassthrough,
  ptActions,
  setPtActions,
  ptHeadline,
  setPtHeadline,
  ptSplit,
  setPtSplit,
  ptFlat,
  setPtFlat,
  ptCapMode,
  setPtCapMode,
  ptCapPct,
  setPtCapPct,
  ptSaving,
  onClose,
  onSave,
}) {
  const isCpc = Boolean(editingPassthrough?._cpcMode);
  const capModeActive = !isCpc && ptCapMode;

  return (
    <Dialog
      open={Boolean(editingPassthrough)}
      onOpenChange={(open) => { if (!open) onClose(); }}
    >
      <DialogContent className="max-w-2xl" data-testid={isCpc ? "edit-cpc-dialog" : "edit-passthrough-dialog"}>
        <DialogHeader>
          <DialogTitle>
            {isCpc ? "Edit CPC payout" : "Edit passthrough payout"}
            {editingPassthrough && (
              <span className="ml-2 text-slate-500 font-normal text-sm">
                — {editingPassthrough.brand_name}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {isCpc ? (
              <>
                CPC offers pay per click — set the publisher-facing label
                (e.g. <span className="font-mono">upto $0.25</span>), optional{" "}
                <span className="font-mono">pub_flat_amount</span>, and the
                split. Earnings settle as <span className="font-mono">upstream CPC × split</span>{" "}
                automatically; no commission % required.
              </>
            ) : capModeActive ? (
              <>
                <strong>Fixed-rate cap.</strong> Publisher earns{" "}
                <span className="font-mono">min(sale × cap %, network payout)</span>{" "}
                per conversion. Low-rate sales pass through 100% (no shave);
                high-rate sales are capped at the cap % (we keep the
                difference). Same shape as Best Buy.
              </>
            ) : (
              <>
                Update the per-action payout breakdown. At least one action with a parseable value
                (e.g. <span className="font-mono">35%</span>,{" "}
                <span className="font-mono">$4-$16</span>) or a headline is required so publisher
                feed ingestion stays parseable.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Fixed-rate cap toggle — only shown for passthrough rows (not CPC).
            Flipping ON converts the assignment to fixed_pct on save. */}
        {editingPassthrough && !isCpc && (
          <div
            className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 mt-2"
            data-testid="pt-edit-capmode-row"
          >
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-800">
                Cap publisher rate (Best-Buy-style)
              </div>
              <div className="text-[11px] text-slate-500 leading-snug">
                Switch from passthrough × split → <span className="font-mono">min(sale × cap %, payout)</span>.
                Use this when you want to advertise a fixed pub rate even though the network pays variable.
              </div>
            </div>
            <Switch
              checked={Boolean(ptCapMode)}
              onCheckedChange={setPtCapMode}
              data-testid="pt-edit-capmode-toggle"
            />
          </div>
        )}

        {editingPassthrough && !isCpc && capModeActive && (
          <div className="space-y-3 pt-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-700 font-bold mb-1 block">
                Pub rate cap %<span className="ml-1 text-rose-600">*</span>
              </label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={ptCapPct}
                onChange={(e) => setPtCapPct(e.target.value)}
                placeholder="e.g. 8.6"
                className="w-32 font-mono h-9"
                data-testid="pt-edit-cap-pct"
              />
              <p className="text-[11px] text-slate-500 mt-1.5">
                Publisher will see <span className="font-mono">{ptCapPct || "X"}%</span> rate.
                On a $100 sale where the network pays{" "}
                <span className="font-mono">$5</span> → pub earns{" "}
                <span className="font-mono">$5.00</span> (pass-through, low rate).
                On a $100 sale where the network pays{" "}
                <span className="font-mono">$12</span> → pub earns{" "}
                <span className="font-mono">
                  ${ptCapPct ? Math.min(parseFloat(ptCapPct), 12).toFixed(2) : "—"}
                </span>{" "}
                (capped). We keep the difference.
              </p>
            </div>
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
              <p className="text-[11px] text-amber-800 leading-snug">
                <strong>Switching to cap mode</strong> will clear the payout breakdown rows below
                (they only apply to true passthrough) and overwrite{" "}
                <span className="font-mono">payout_mode = fixed_pct</span> on save.
              </p>
            </div>
          </div>
        )}

        {editingPassthrough && !isCpc && !capModeActive && (
          <PayoutBreakdownEditor
            payoutActions={ptActions}
            setPayoutActions={setPtActions}
            headlinePayout={ptHeadline}
            setHeadlinePayout={setPtHeadline}
            splitPct={ptSplit}
            setSplitPct={setPtSplit}
            idPrefix="pt-edit"
          />
        )}

        {editingPassthrough && isCpc && (
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                Publisher-facing CPC label
              </label>
              <Input
                value={ptHeadline}
                onChange={(e) => setPtHeadline(e.target.value)}
                placeholder="upto $0.25"
                className="font-mono"
                data-testid="cpc-edit-display-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                Free-form text the publisher sees alongside their split — does not
                affect earnings math, only the display.
              </p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                Pub flat $ <span className="font-normal normal-case text-slate-400">(pub_flat_amount)</span>
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={ptFlat}
                onChange={(e) => setPtFlat(e.target.value)}
                placeholder="e.g. 0.25"
                className="w-32 font-mono"
                data-testid="cpc-edit-flat-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                Optional flat CPC amount stored on the assignment. Leave blank for 0.
              </p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                Publisher split %
              </label>
              <Input
                type="number"
                step="1"
                min="1"
                max="100"
                value={ptSplit}
                onChange={(e) => setPtSplit(e.target.value)}
                className="w-24 font-mono"
                data-testid="cpc-edit-split-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                Publisher earns this % of the upstream CPC per click.
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={ptSaving}
            data-testid="pt-edit-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={ptSaving}
            className="bg-[#002FA7] hover:bg-[#002089] text-white"
            data-testid="pt-edit-save"
          >
            {ptSaving
              ? "Saving…"
              : isCpc
              ? "Save CPC"
              : capModeActive
              ? "Save fixed-rate cap"
              : "Save passthrough"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
